import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import StagingBanner from "./components/StagingBanner";
import Login from "./pages/Login";
import Shops from "./pages/Shops";
import ShopNew from "./pages/ShopNew";
import ShopDetail from "./pages/ShopDetail";
import Plans from "./pages/Plans";
import Analytics from "./pages/Analytics";
import ProductTemplates from "./pages/ProductTemplates";
import TemplateDetail from "./pages/TemplateDetail";
import Signups from "./pages/Signups";
import ProblemReports from "./pages/ProblemReports";
import Referrals from "./pages/Referrals";
import Releases from "./pages/Releases";
import CatalogRequests from "./pages/CatalogRequests";
import Activation from "./pages/Activation";
import PlatformSettings from "./pages/PlatformSettings";

/**
 * Gates on the role, not just a truthy user — a shop owner's perfectly valid
 * token must not get into the platform-admin app. AuthContext already refuses
 * to keep a non-super_admin session, so this is belt and suspenders, but it's
 * the one this app actually enforces (admin-dashboard's equivalent is dead
 * code today).
 */
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600"></div>
      </div>
    );
  }

  return user?.role === "super_admin" ? children : <Navigate to="/login" replace />;
};

function App() {
  return (
    <AuthProvider>
      <StagingBanner />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="/shops" replace />} />
            <Route path="shops" element={<Shops />} />
            <Route path="shops/new" element={<ShopNew />} />
            <Route path="shops/:id" element={<ShopDetail />} />
            <Route path="signups" element={<Signups />} />
            <Route path="problem-reports" element={<ProblemReports />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="releases" element={<Releases />} />
            <Route path="plans" element={<Plans />} />
            <Route path="settings" element={<PlatformSettings />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="activation" element={<Activation />} />
            <Route path="product-templates" element={<ProductTemplates />} />
            <Route path="catalog-requests" element={<CatalogRequests />} />
            <Route path="product-templates/:id" element={<TemplateDetail />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
